import {
  BrandMediaInfo,
  ImageSize,
  ImageSizes,
  MediaContentType,
  MediaUrlOptions,
  PropertyMediaInfo,
} from './image.models';
import { defaultImagePlaceholder, imageTypes } from './image.const';

/**
 * Helper function that analyses if a media info object, has a valid status to
 * be able to get published online.
 *
 * @param recordMediaInfoArray Record to be analysed
 * @return This dictates if the media is publishable or not
 */
export function isMediaInfoPublishable(recordMediaInfoArray: PropertyMediaInfo): boolean {
  return (
    recordMediaInfoArray == null ||
    !(recordMediaInfoArray.watermark_label === 'CostarWaterMark' || recordMediaInfoArray.publish_image_f === false)
  );
}

export function extractFileExtension(media_path: string): string {
  const splitValues = media_path.split('.');
  return splitValues[splitValues.length - 1];
}

export function isMediaTypeImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return mediaRecord.media_type_desc === 'Image';
}
export function isMediaExtensionImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  const mediaExtension = extractFileExtension(mediaRecord.media_path);
  return imageTypes.some(type => type === mediaExtension.toLowerCase());
}

/**
 * Review is media record type is Image & it's extension coincides with image
 * extension
 */
export function isMediaImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return isMediaTypeImage(mediaRecord) && isMediaExtensionImage(mediaRecord);
}

/**
 * This function formats a passed url with a proportioned prefix. It uses a url
 * splitter string to split the url into pieces so it can use the later section
 * of the split url and append the provided prefix.
 *
 * @example
 * ```
 * mediaUrlFormatter('prop', 'miq.com/aws/', '/prop/3/534/image.jpg')
 * // result = 'miq.com/aws/3/534/image.jpg'
 * mediaUrlFormatter('prop', 'miq.com/aws/', 'aws.com/prop/3/534/image.jpg')
 * // result = 'miq.com/aws/3/534/image.jpg'
 *
 * @returns urlPrefix + splitValueWithoutFirstSlash
 * ```
 */
function mediaUrlFormatter(urlSplitter: string, urlPrefix: string, url: string): string {
  let splitValues = url.split(urlSplitter);
  const urlValue = splitValues[1] === '/' ? splitValues[2] : splitValues[1];
  let prefix = getImagePrefix(urlPrefix, url);
  return `${prefix}${urlValue.substr(1)}`;
}

/**
 * Wrap {@link mediaUrlFormatter} that returns a default image placeholder if
 * flag is on.
 *
 * Apply some validations before formatting url.
 *
 * @see mediaUrlFormatter
 */
function formatImageUrl(urlSplitter: string, urlPrefix: string, url: string, provideDefault = true): string {
  let imageUrl = provideDefault ? defaultImagePlaceholder : null;
  if (url != null && url.includes(urlSplitter)) {
    imageUrl = mediaUrlFormatter(urlSplitter, urlPrefix, url);
  }
  return imageUrl;
}

/**
 * Generate a URL string using the provided original URL. Internally, it uses
 * {@link formatImageUrl} to format the expected URL.
 * @param splitter
 * @param prefix
 * @param url
 * @param options
 */
export function genOriginalMediaURI(
  splitter: string,
  prefix: string,
  url: string,
  options: { provideDefault?: boolean } = {}
) {
  const { provideDefault = true } = options;
  return formatImageUrl(splitter, prefix, url, provideDefault);
}

/**
 * Generate the thumbnail URL from the input URL. Internally, it uses
 * {@link formatImageUrl} to generate and format the URL. The resulting URI
 * will contain the prefix followed by the requested image size (**defaulted
 * to medium**).
 *
 * prefix + imageSize + '/'
 *
 * @see ImageSize
 * @see ImageSizes
 */
export function genThumbMediaURI(
  splitter: string,
  prefix: string,
  url: string,
  options: MediaUrlOptions & { provideDefault?: boolean } = {}
): string {
  const { provideDefault = true, imgSize = ImageSize.Medium } = options;
  const aliOSSBase = '.oss-accelerate.aliyuncs.com';
  let newEdpMultimediaKey = splitter;
  if (url && url.includes(aliOSSBase) && !newEdpMultimediaKey.includes(aliOSSBase)) {
    newEdpMultimediaKey = `${newEdpMultimediaKey}${aliOSSBase}`;
  }
  return formatImageUrl(newEdpMultimediaKey, `${prefix}${ImageSizes[imgSize]}/`, url, provideDefault);
}

/**
 * The two `environment.media` values every MIQ shell already owns, bundled so callers can pass
 * them as one argument.
 *
 * L2-S1/T3: this library is deliberately DI-free and cannot import `@env/environment`, so the
 * media location comes in as data. `environment.media` is structurally assignable to this, i.e.
 * shells pass `environment.media` directly.
 */
export interface MediaLocationConfig {
  /** Path fragment that splits a stored media path into origin + key (`environment.media.edpMultimediaKey`). */
  edpMultimediaKey: string;
  /** Bucket/CDN prefix that serves thumbnail renditions (`environment.media.thumbLocationURI`). */
  thumbLocationURI: string;
}

/**
 * Thumbnail URL for a stored media path.
 *
 * L2-S1/T3: moved out of `src/app/records/records-search/esri-map/card-view-factory.ts`, whose
 * only job was to read the two values below off `environment` and delegate to
 * {@link genThumbMediaURI}. Living here lets the reports remote generate thumbnails without an
 * `@records` import; the host keeps a 2-argument wrapper of the same name so its ~13 existing
 * call sites are untouched.
 */
export function genThumbnailUrl(
  config: MediaLocationConfig,
  url: string,
  options: MediaUrlOptions & { provideDefault?: boolean } = {}
): string {
  return genThumbMediaURI(config.edpMultimediaKey, config.thumbLocationURI, url, options);
}

/**
 * Normalise one media record for display: derive its extension, classify it as image vs
 * attachment, stamp its 1-based display index, and — for images only — attach a small
 * thumbnail URL.
 *
 * L2-S1/T3: moved out of
 * `@records/.../record-attachments/attachments-table/attachment-table.config`, a module that
 * value-imports `AttachmentsTableComponent` and therefore reached ~1400 host files. The
 * clipboard helper (and the reports remote through it) only ever wanted these 11 lines.
 *
 * NOTE the config comes FIRST: the host wrapper is used as `mediaList.map(genThumbnailsToMedia)`,
 * so the (media, index) pair must stay in `Array.prototype.map` order and a trailing parameter
 * would silently receive the array.
 *
 * Mutates `media` in place (as it always has) and returns either it or an image-shaped copy.
 */
export function genThumbnailsToMedia(
  config: MediaLocationConfig,
  media: PropertyMediaInfo | BrandMediaInfo,
  index: number
) {
  media.file_extension = extractFileExtension(media.media_path);
  const isImage = isMediaImage(media);
  media.file_type = isImage ? 'Image' : 'Attachment';
  media['displayIndex'] = index + 1;
  media['index'] = index;
  if (isImage) {
    return { ...media, sanitized_media_path: genThumbnailUrl(config, media.media_path, { imgSize: ImageSize.Small }) };
  }
  return media;
}

export function getPrimaryImageOrFirst(
  recordMediaArray: Array<PropertyMediaInfo | BrandMediaInfo>
): PropertyMediaInfo | BrandMediaInfo {
  let image = getPrimaryImage(recordMediaArray);
  if (!image && recordMediaArray != null && recordMediaArray.length) {
    const sortedImages = ((Array.isArray(recordMediaArray) && recordMediaArray) || [])
      .filter(isMediaImage)
      .sort((imageA, imageB) => imageB.id - imageA.id);
    image = sortedImages.length > 0 ? sortedImages[0] : null;
  }
  return image;
}

export function getImageByType(
  recordMediaArray: Array<PropertyMediaInfo | BrandMediaInfo>
): PropertyMediaInfo | BrandMediaInfo {
  const sortedImages = recordMediaArray.slice().sort((a, b) => b.id - a.id);
  const imageByType = sortedImages.find(recordMedia => {
    const { media_content_type_desc } = recordMedia as PropertyMediaInfo;
    return media_content_type_desc === MediaContentType.FloorPlan;
  });
  return imageByType;
}

export function getPrimaryImage(mediaRecords: Array<PropertyMediaInfo | BrandMediaInfo>) {
  return (mediaRecords || [])
    .slice()
    .sort((a, b) => b.id - a.id)
    .find(recordMedia => recordMedia.primary_image_f && isMediaImage(recordMedia));
}

export function getImagePrefix(prefix: string, url: string): string {
  let newPrefix = url.includes('aliyuncs.com') ? prefix.replace('prop-images', 'prop-images-cn') : prefix;
  return newPrefix;
}
